import {construirePayload, coutKmValide, estEnCours, figerJour, formatEuros, formatHeures, formatKm, pointPourPayload, soumettreScenario} from './simulationRules';

/**
 * TOUR-03 lot 4 — règles pures de l'atelier sur carte (mandat-lot4b-front.md, A-1 à A-8).
 * Aucune dépendance React : les composants ne font qu'afficher ce que ce fichier décide.
 * Les noms de champs viennent de l'API (SimulationCalques, SimulationTourneesCalques, Simulation).
 */

/* ------------------------------------------------------------------ A-1 : entrée par client */

const idDe = (v) => {
    if (v == null || v === '') return null;
    if (typeof v === 'object') return idDe(v['@id'] ?? v.id);
    const s = String(v);
    return s.includes('/') ? s.split('/').filter(Boolean).pop() : s;
};

/**
 * Sélection de l'entrée (`?client=&patrimoine=`) après le choix d'un client : changer de client vide le patrimoine.
 * Choisir le même client ne touche à rien.
 */
export function selectionApresClient(courante, clientId) {
    const client = clientId == null || clientId === '' ? null : String(clientId);
    if (client === (courante?.client ?? null)) return {client, patrimoine: courante?.patrimoine ?? null};
    return {client, patrimoine: null};
}

/**
 * Patrimoines proposés pour le client : l'API filtre déjà (`/patrimoines?client=`), ce garde-fou écarte toute ligne qui
 * porte un autre client (si la ligne ne porte pas de client, elle est conservée) et sans client choisi la liste est vide.
 */
export function patrimoinesDuClient(patrimoines, clientId) {
    const cid = idDe(clientId);
    if (cid == null) return [];
    return (patrimoines || []).filter((p) => p?.client == null || idDe(p.client) === cid);
}

/** Message sous le sélecteur de patrimoine : consigne sans client, « aucun » pour un client sans patrimoine. */
export function messagePatrimoines(clientId, liste, chargement = false, erreur = false) {
    if (clientId == null || clientId === '') return 'Choisissez un client pour afficher ses patrimoines.';
    if (chargement) return 'Chargement des patrimoines…';
    if (erreur) return 'Impossible de charger les patrimoines.';
    return liste.length === 0 ? 'Aucun patrimoine pour ce client.' : null;
}

/* ------------------------------------------------------------------ lot 8 : mémoire du couple client / patrimoine */

export const CLE_SELECTION = 'simulationsSelection';

/** Dernier couple mémorisé `{client, patrimoine}`, ou null. Stockage vide, illisible ou bloqué : null, jamais une erreur. */
export function lireSelection(stockage) {
    try {
        const s = JSON.parse(stockage.getItem(CLE_SELECTION));
        return s?.client ? {client: String(s.client), patrimoine: s.patrimoine ? String(s.patrimoine) : null} : null;
    } catch {
        return null;
    }
}

/** Mémorise le couple ; sans client, efface la mémoire (l'utilisateur a vidé sa sélection). */
export function ecrireSelection(stockage, {client, patrimoine}) {
    try {
        if (client) stockage.setItem(CLE_SELECTION, JSON.stringify({client, patrimoine: patrimoine ?? null}));
        else stockage.removeItem(CLE_SELECTION);
    } catch {
        // stockage bloqué : l'URL reste la source de vérité
    }
}

/** Sélection à remettre dans l'URL : seulement quand l'URL n'en porte aucune (clic sur le menu). Une URL explicite prime. */
export function selectionARestaurer(url, memorisee) {
    return url?.client || url?.patrimoine ? null : memorisee;
}

/**
 * Sélection de l'URL une fois les listes chargées : ce qui n'existe plus (ou plus pour cet utilisateur) est retiré. Retourne
 * le couple corrigé, ou null s'il n'y a rien à corriger. Sans client, le patrimoine n'est pas contrôlable : on n'y touche pas.
 * `clients` / `patrimoines` : null tant que la liste n'est pas chargée.
 */
export function selectionACorriger({client, patrimoine}, clients, patrimoines) {
    if (!client) return null;
    if (clients && !clients.some((c) => String(c.id) === String(client))) return {client: null, patrimoine: null};
    if (patrimoine && patrimoines && !patrimoineDeLaListe(patrimoines, patrimoine)) return {client, patrimoine: null};
    return null;
}

/** Le patrimoine de l'URL appartient-il bien à la liste du client ? (sinon le sélecteur reste vide) */
export function patrimoineDeLaListe(liste, patrimoineId) {
    if (patrimoineId == null || patrimoineId === '') return null;
    return (liste || []).find((p) => String(p.id) === String(patrimoineId)) ?? null;
}

/** Paramètres d'URL de l'entrée / de l'atelier, sans valeur vide. */
export function paramsUrl(params) {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(params || {})) {
        if (v != null && v !== '') q.set(k, String(v));
    }
    const s = q.toString();
    return s ? `?${s}` : '';
}

/* ------------------------------------------------------------------ volet : sections */

export const SECTIONS = [
    {id: 'periode', titre: 'Période'},
    {id: 'equipes', titre: 'Équipes, équipage, base, déplacement'},
    {id: 'collecte', titre: 'Collecte'},
    {id: 'budgets', titre: 'Budgets'},
    {id: 'couts', titre: 'Coûts'},
    {id: 'verrous', titre: 'Verrous'},
    {id: 'avance', titre: 'Réglages avancés'},
];

const RACINES_SECTION = {
    libelle: 'periode', du: 'periode', au: 'periode',
    nbEquipes: 'equipes', equipage: 'equipes', base: 'equipes', depot: 'equipes', deplacement: 'equipes',
    contraint: 'collecte', tolerances: 'collecte',
    heuresParJour: 'budgets', amplitudeMaxH: 'budgets', joursParSemaine: 'budgets', horizonJournees: 'budgets', horizonVerrouille: 'budgets',
    coutHoraire: 'couts', forfaitDeplacement: 'couts', coutKm: 'couts',
    interdits: 'verrous', jours: 'verrous',
};

/** Section du volet qui porte un chemin Formik (tout le reste = réglages avancés). */
export function sectionDuChamp(chemin) {
    return RACINES_SECTION[String(chemin).split('.')[0]] ?? 'avance';
}

/** Sections à déplier pour montrer les erreurs (validation locale ou 422 imbriquées), dans l'ordre du volet. */
export function sectionsEnErreur(erreurs) {
    const ids = new Set(Object.keys(erreurs || {}).map(sectionDuChamp));
    return SECTIONS.map((s) => s.id).filter((id) => ids.has(id));
}

/* ------------------------------------------------------------------ A-5 : Jouer / Enregistrer */

const deux = (n) => String(n).padStart(2, '0');

/** Libellé du brouillon : celui du volet s'il est saisi, sinon « Brouillon JJ/MM HH:MM » (l'API exige 1 à 120 caractères). */
export function libelleBrouillon(valeurs, maintenant = new Date()) {
    const saisi = (valeurs?.libelle || '').trim();
    if (saisi) return saisi.slice(0, 120);
    return `Brouillon ${deux(maintenant.getDate())}/${deux(maintenant.getMonth() + 1)} ${deux(maintenant.getHours())}:${deux(maintenant.getMinutes())}`;
}

/** Jouer exige les mêmes minima que « Lancer » du wizard, sans le libellé : €/km, période, base de départ. */
export function peutJouer(valeurs) {
    return coutKmValide(valeurs?.coutKm) && !!valeurs?.du && !!valeurs?.au && pointPourPayload(valeurs?.base) != null;
}

/** Corps du POST /simulations d'un « Jouer » : le payload du wizard, avec un libellé de brouillon. */
export function payloadJouer(valeurs, patrimoine, maintenant = new Date(), rejouerDe = null) {
    return construirePayload({...valeurs, libelle: libelleBrouillon(valeurs, maintenant)}, patrimoine, rejouerDe);
}

/**
 * Jouer = un POST brouillon (toujours un nouveau : un scénario enregistré ne se modifie jamais).
 * Retourne `{cree}` ou, sur une 422, `{erreurs, globales}` (soumettreScenario, commun au wizard).
 */
export function jouerScenario(creer, valeurs, patrimoine, maintenant = new Date(), rejouerDe = null) {
    return soumettreScenario(creer, valeurs, patrimoine, (v, p) => payloadJouer(v, p, maintenant, rejouerDe));
}

/** Enregistrer = POST /simulations/{id}/enregistrer avec le libellé saisi (vide : pas de renommage). */
export async function enregistrerScenario(enregistrer, id, libelle) {
    const propre = (libelle || '').trim();
    return enregistrer(propre ? {id, libelle: propre} : {id});
}

/** Libellé proposé à l'enregistrement : celui du volet, sinon celui du brouillon. */
export function libelleProposeEnregistrement(valeurs, simulation) {
    const saisi = (valeurs?.libelle || '').trim();
    return saisi || simulation?.libelle || '';
}

/** Libellé valide pour l'API : 1 à 120 caractères. */
export const libelleValide = (libelle) => {
    const l = (libelle || '').trim();
    return l.length >= 1 && l.length <= 120;
};

/** Enregistrer est actif quand le scénario affiché est `done` et pas encore enregistré. */
export function peutEnregistrer(simulation) {
    return !!simulation && simulation.statut === 'done' && simulation.enregistre === false;
}

/** Bouton Jouer : « Calcul… » grisé pendant l'envoi ou tant que le scénario affiché est pending/processing. */
export function etatBoutonJouer({envoi = false, simulation = null, peut = true}) {
    const calcul = envoi || (!!simulation && estEnCours(simulation.statut));
    return {label: calcul ? 'Calcul…' : 'Jouer', disabled: calcul || !peut, calcul};
}

/* ------------------------------------------------------------------ A-8 : sortie de l'atelier, ouverture */

/** Un brouillon (calculé ou en cours) serait purgé à la sortie : confirmation simple. Un enregistré ou un échec, non. */
export function doitConfirmerSortie(simulation) {
    return !!simulation && simulation.enregistre === false && simulation.statut !== 'failed';
}

/**
 * Le scénario dont les calques s'affichent à l'ouverture : `?from=` (le source rejoue ses calques), sinon rien.
 * Le volet est pré-rempli par `valeursOuverture(source, figer)` (simulationRules).
 */
export function afficheeALOuverture(fromId) {
    return fromId == null || fromId === '' ? null : String(fromId);
}

/* ------------------------------------------------------------------ calques : palette, collecte (A-2), tournées (A-3) */

// Palette des calques (8 couleurs, cyclique). Sept viennent des tokens de GUIDE_UI / DESIGN.md ; la huitième
// (#2F80C0) est un ajout à valider. Le rouge (#B22222) est réservé aux visites non affectées, le gris au patrimoine.
export const PALETTE_ATELIER = ['#213067', '#7BB421', '#B1560F', '#F39A3F', '#9D23D1', '#538A85', '#E0A100', '#2F80C0'];
export const COULEUR_NON_AFFECTE = '#B22222';
export const COULEUR_PATRIMOINE = '#8A929C';

export const couleurCalque = (index) => {
    const n = PALETTE_ATELIER.length;
    return PALETTE_ATELIER[(((Number(index) || 0) % n) + n) % n];
};

export const JOURS_SEMAINE = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
const JOURS_COURTS = ['dim.', 'lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.'];
export const FREQUENCES = ['hebdo', 'paire', 'impaire', 'quotidien'];
// Pictogramme de parité (caractères géométriques, pas d'emoji) : plein = chaque semaine, anneau = paire, mi-teinte = impaire.
export const PICTO_PARITE = {hebdo: '●', paire: '○', impaire: '◐', quotidien: '◆'};

export const libelleJour = (jour) => JOURS_SEMAINE[Number(jour) - 1] ?? 'Tous les jours';
export const libellePav = (n) => `${n} PAV`;

const rangFrequence = (f) => {
    const i = FREQUENCES.indexOf(f);
    return i < 0 ? FREQUENCES.length : i;
};

/**
 * A-2 — un calque par (jour, parité) de `GET /simulations/calques`, avec sa couleur (par jour) et son nombre de PAV
 * (distincts : un PAV à deux flux le même jour compte une fois). Légende triée lundi → dimanche puis hebdo, paire, impaire.
 */
export function calquesCollecte(donnees) {
    const groupes = new Map();
    for (const pav of donnees?.pavs || []) {
        for (const l of pav.collecte || []) {
            const cle = `${l.jour}-${l.frequence}`;
            if (!groupes.has(cle)) groupes.set(cle, {cle, jour: Number(l.jour), frequence: l.frequence, nomenclatures: new Set()});
            groupes.get(cle).nomenclatures.add(pav.nomenclature);
        }
    }
    return [...groupes.values()]
        .sort((a, b) => a.jour - b.jour || rangFrequence(a.frequence) - rangFrequence(b.frequence))
        .map((g) => ({
            cle: g.cle,
            jour: g.jour,
            frequence: g.frequence,
            couleur: couleurCalque(g.jour - 1),
            picto: PICTO_PARITE[g.frequence] ?? PICTO_PARITE.hebdo,
            nbPav: g.nomenclatures.size,
            nomenclatures: [...g.nomenclatures],
            libelle: `${libelleJour(g.jour)} · ${g.frequence} · ${libellePav(g.nomenclatures.size)}`,
        }));
}

/** « ven. 09/10 » pour `2026-10-09` (sans fuseau). */
export function formatDateCourte(date) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date || '');
    if (!m) return date ?? '';
    const jour = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))).getUTCDay();
    return `${JOURS_COURTS[jour]} ${m[3]}/${m[2]}`;
}

const positionDe = (p) => (p && p.lat != null && p.lon != null ? [Number(p.lat), Number(p.lon)] : null);

/**
 * A-3 — un calque par journée de `GET /simulations/{id}/calques` : couleur `couleurIndex` (palette de 8, cyclique),
 * points numérotés dans l'ordre de l'API (`ordre`), tracé des visites et segments base en pointillé seulement si le
 * trajet du jour porte un départ ou un retour.
 */
export function calquesTournees(donnees) {
    const journees = [...(donnees?.journees || [])]
        .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : a.equipe - b.equipe));
    const equipesParDate = new Map();
    journees.forEach((j) => equipesParDate.set(j.date, (equipesParDate.get(j.date) || 0) + 1));
    return journees.map((j, i) => {
        const points = [...(j.points || [])]
            .sort((a, b) => a.ordre - b.ordre)
            .map((p) => ({
                numero: p.ordre, nomenclature: p.nomenclature, position: [p.lat, p.lon],
                arrivee: p.arrivee ?? null, partie: p.partie ?? null, fige: !!p.fige,
            }));
        const trace = points.map((p) => p.position);
        const depart = positionDe(j.trajet?.depart);
        const retour = positionDe(j.trajet?.retour);
        const segmentsBase = [];
        if (points.length > 0) {
            if (depart) segmentsBase.push([depart, points[0].position]);
            if (retour) segmentsBase.push([points[points.length - 1].position, retour]);
        }
        const doubleEquipe = equipesParDate.get(j.date) > 1;
        return {
            cle: `${j.date}|${j.equipe}`,
            rang: i + 1,
            date: j.date,
            equipe: j.equipe,
            couleur: couleurCalque(j.couleurIndex),
            bacs: j.bacs,
            signal: j.signal ?? null,
            points,
            trace,
            segmentsBase,
            depart,
            retour,
            aTrajet: segmentsBase.length > 0,
            libelle: `J${i + 1} · ${formatDateCourte(j.date)}${doubleEquipe ? ` · équipe ${j.equipe}` : ''} · ${pavEtBacs(points.length, j.bacs)}`,
        };
    });
}

const LIBELLES_METHODE_TRACE = {routier: 'routier', vol_oiseau: "vol d'oiseau"};

/**
 * Lot 8 : tracé d'une équipe dans `GET /simulations/{id}/journees/{date}/trace`, positions Leaflet [lat, lon] (GeoJSON = [lon, lat]).
 * `routier` : la géométrie va de la base à la base, elle remplace le tracé droit et les segments base. Null : pas de réponse (encore).
 * Les km affichés restent ceux du solveur : `distanceKm` n'est jamais montré.
 */
export function traceDeLEquipe(donnees, equipe) {
    const t = (donnees?.traces || []).find((x) => x.equipe === equipe);
    if (!t) return null;
    return {
        methode: t.methode,
        libelle: LIBELLES_METHODE_TRACE[t.methode] ?? t.methode,
        routier: t.methode === 'routier',
        positions: (t.geometrie?.coordinates || []).map(([lon, lat]) => [lat, lon]),
    };
}

/** Visites non affectées en rouge : seulement celles qui ont des coordonnées. */
export function pointsNonAffectes(donnees) {
    return (donnees?.nonAffectees || [])
        .filter((n) => n.lat != null && n.lon != null)
        .map((n) => ({nomenclature: n.nomenclature, position: [Number(n.lat), Number(n.lon)], bacs: n.bacs, raison: n.raison}));
}

/** A-6 — nomenclatures de chaque journée (par date) : pour constater qu'un PAV a changé de journée après un rejeu. */
export function pavsParDate(calques) {
    const r = {};
    for (const c of calques || []) {
        r[c.date] = [...new Set([...(r[c.date] || []), ...c.points.map((p) => p.nomenclature)])];
    }
    return r;
}

/* ------------------------------------------------------------------ calques : visibilité */

export const cleCalque = {
    patrimoine: 'patrimoine',
    collecte: (cle) => `collecte:${cle}`,
    tournee: (cle) => `tournee:${cle}`,
};

/**
 * Visibilité d'un calque : le choix explicite de l'utilisateur d'abord ; sinon par défaut le patrimoine est visible,
 * la collecte l'est tant qu'aucun scénario n'est joué, et les tournées le sont dès qu'il y en a.
 */
export function calqueVisible(cle, choix, {aTournees}) {
    if (choix && cle in choix) return !!choix[cle];
    if (cle.startsWith('collecte:')) return !aTournees;
    return true;
}

/* ------------------------------------------------------------------ A-4 : bandeau verdict */

const unite = (n, singulier, pluriel) => (Number(n) >= 2 ? pluriel : singulier);

/**
 * Lot 6 (C-1) : « N PAV · B bacs » côte à côte partout (journée, légende, verdict). Une quantité absente disparaît seule ;
 * les deux absentes → chaîne vide (pas de bloc vide).
 */
export function pavEtBacs(pav, bacs) {
    return [
        pav == null ? null : `${Number(pav).toLocaleString('fr-FR')} PAV`,
        bacs == null ? null : `${Number(bacs).toLocaleString('fr-FR')} ${unite(bacs, 'bac', 'bacs')}`,
    ].filter(Boolean).join(' · ').replace(/[  ]/g, ' ');
}

/**
 * Lot 6 (C-2) : pilule des distances d'un scénario selon `totaux.distances` (`osrm` | `vol_oiseau`). L'aide n'existe qu'en vol
 * d'oiseau (les km sont une estimation). Valeur absente ou inconnue (ancien résultat) : pas de pilule.
 */
export function pastilleDistances(distances) {
    if (distances === 'osrm') return {libelle: 'Distances : routier (OSRM)', aide: null, etat: 'osrm'};
    if (distances === 'vol_oiseau') {
        return {libelle: "Distances : vol d'oiseau × 1,3", aide: 'les journées peuvent paraître dispersées, les km sont une estimation', etat: 'vol_oiseau'};
    }
    return null;
}

/** Version courte pour le bandeau de l'atelier : « Routier » / « Vol d'oiseau », libellé complet (et aide) en infobulle. */
export function pastilleDistancesCourte(distances) {
    const p = pastilleDistances(distances);
    if (!p) return null;
    return {libelle: p.etat === 'osrm' ? 'Routier' : 'Vol d\x27oiseau', infobulle: [p.libelle, p.aide].filter(Boolean).join(' — '), etat: p.etat};
}

/** Couleurs d'état de GUIDE_UI (Couleurs d'état) : vert = rien à signaler, orange = dépassements, rouge = échec. */
export const VERDICT_STYLES = {
    vert: {dot: '#87BC26', titleColor: '#5C8316', gradient: 'linear-gradient(135deg, #FBFEF6, #EDF4DB 55%, #DCEAB8)', border: '#CFE2A0', icon: 'check'},
    orange: {dot: '#F39A3F', titleColor: '#B8641A', gradient: 'linear-gradient(135deg, #FFFBF6, #FDEBD8 55%, #F9D3AE)', border: '#F5C89A', icon: 'warning'},
    rouge: {dot: '#B22222', titleColor: '#B22222', gradient: 'linear-gradient(135deg, #FFF8F8, #FBE6E6 55%, #F3C3C3)', border: '#EBA8A8', icon: 'close'},
    depasse: {dot: '#B22222', titleColor: '#B22222', gradient: 'linear-gradient(135deg, #FFF8F8, #FBE6E6 55%, #F3C3C3)', border: '#EBA8A8', icon: 'error'},
    calcul: {dot: '#8A929C', titleColor: '#5A6470', gradient: 'linear-gradient(135deg, #FBFCFA, #F3F6EF 55%, #E6EAEE)', border: '#E3E7EC', icon: 'hourglass'},
};

const espaces = (t) => t.replace(/[  ]/g, ' ');

/**
 * Dépassements d'un scénario : une journée dont le budget d'heures est dépassé (`signal` non nul) compte pour un, le
 * signal de campagne (horizon dépassé, visites non affectées) pour un de plus. Un dépassement = orange.
 */
export function compterDepassements(simulation) {
    const parJournee = (simulation?.journees || []).filter((j) => j.signal != null && j.signal !== '').length;
    return parJournee + (simulation?.totaux?.signal ? 1 : 0);
}

/** Lot 5 (B-1) : `totaux.niveau` de l'API pilote la couleur ; absent ou inconnu → repli sur le comptage local des dépassements. */
export const NIVEAU_ETAT = {ok: 'vert', attention: 'orange', depasse: 'rouge'};

export function etatDuNiveau(niveau, depassements) {
    return NIVEAU_ETAT[niveau] ?? (depassements > 0 ? 'orange' : 'vert');
}

/**
 * Lot 5 : visites non affectées regroupées par raison (« période épuisée », « horizon imposé atteint »…), pour les lister
 * sous le verdict. `[{raison, nombre, nomenclatures}]`, raisons dans l'ordre d'apparition ; vide si aucune.
 */
export function groupesNonAffectees(simulation) {
    const groupes = new Map();
    for (const n of simulation?.nonAffectees?.liste ?? []) {
        const raison = n.raison || 'raison inconnue';
        if (!groupes.has(raison)) groupes.set(raison, {raison, nombre: 0, nomenclatures: []});
        const g = groupes.get(raison);
        g.nombre += 1;
        if (!g.nomenclatures.includes(n.nomenclature)) g.nomenclatures.push(n.nomenclature);
    }
    return [...groupes.values()];
}

/** « 3 visites non affectées — période épuisée : COGOM001, COGOM002, COGCS003 » (au plus 5 PAV nommés, puis « … »). */
export function lignesNonAffectees(simulation, max = 5) {
    return groupesNonAffectees(simulation).map((g) => {
        const noms = g.nomenclatures.slice(0, max).join(', ') + (g.nomenclatures.length > max ? ', …' : '');
        return `${g.nombre} ${g.nombre > 1 ? 'visites non affectées' : 'visite non affectée'} — ${g.raison} : ${noms}`;
    });
}

/**
 * A-4 — verdict d'un scénario en une ligne : « N journées · H h · K km · B bacs · C € · D dépassements » et son état
 * (vert 0 dépassement, orange ≥ 1, rouge `failed` avec `erreur`, gris tant que le calcul est en cours).
 */
export function verdictSimulation(simulation) {
    if (!simulation) return null;
    if (simulation.statut === 'failed') {
        return {etat: 'rouge', ...VERDICT_STYLES.rouge, texte: 'Échec du calcul', erreur: simulation.erreur ?? null, signal: null, depassements: 0, nonAffectees: 0};
    }
    if (estEnCours(simulation.statut)) {
        const texte = simulation.statut === 'processing' ? 'Calcul en cours…' : 'En attente du calcul…';
        return {etat: 'calcul', ...VERDICT_STYLES.calcul, texte, erreur: null, signal: null, depassements: 0, nonAffectees: 0};
    }
    const t = simulation.totaux || {};
    const depassements = compterDepassements(simulation);
    const etat = etatDuNiveau(t.niveau, depassements);
    const texte = [
        `${t.journees ?? 0} ${unite(t.journees, 'journée', 'journées')}`,
        // Lot 6 : PAV et bacs côte à côte juste après les journées (résultats d'avant le lot 6 : sans PAV).
        t.pav != null ? pavEtBacs(t.pav, t.bacs ?? 0) : `${Number(t.bacs ?? 0).toLocaleString("fr-FR")} ${unite(t.bacs, "bac", "bacs")}`,
        formatHeures(t.heuresTravail),
        formatKm(t.km),
        formatEuros(t.cout?.coutTotal),
        `${depassements} ${unite(depassements, 'dépassement', 'dépassements')}`,
    ].map(espaces).join(' · ');
    return {etat, ...VERDICT_STYLES[etat === 'rouge' ? 'depasse' : etat], texte, erreur: null, signal: t.signal ?? null, depassements, nonAffectees: simulation.nonAffectees?.nombre ?? 0};
}

/* ------------------------------------------------------------------ A-7 : rail de contexte */

export const dateJJMM = (date) => (/^\d{4}-\d{2}-\d{2}$/.test(date || '') ? `${date.slice(8)}/${date.slice(5, 7)}` : date ?? '');

/** « vendredi impaire », « lundi hebdo » ; une collecte quotidienne n'a pas de jour. */
export function libelleCollecte(ligne) {
    if (ligne.frequence === 'quotidien') return 'quotidien';
    return `${libelleJour(ligne.jour).toLowerCase()} ${ligne.frequence}`;
}

/**
 * Affectations d'un PAV dans les calques tournées : une par visite (un PAV peut être visité en deux parties).
 * `texte` = « journée du 09/10 · ordre 12 · 09:27 ».
 */
export function affectationsDuPav(calques, nomenclature) {
    const r = [];
    for (const c of calques || []) {
        for (const p of c.points) {
            if (p.nomenclature !== nomenclature) continue;
            r.push({
                cle: c.cle, date: c.date, equipe: c.equipe, ordre: p.numero, arrivee: p.arrivee, partie: p.partie, fige: p.fige,
                texte: ['journée du ' + dateJJMM(c.date), `ordre ${p.numero}`, p.arrivee].filter(Boolean).join(' · '),
            });
        }
    }
    return r;
}

/** Raison de la visite d'un PAV dans le détail d'une journée (`GET /simulations/{id}/journees/{date}`), ou null. */
export function raisonDeLaVisite(detailJournee, nomenclature, ordre = null) {
    for (const j of detailJournee?.journees || []) {
        for (const v of j.visites || []) {
            if (v.nomenclature === nomenclature && (ordre == null || v.ordre === ordre)) return v.raison ?? null;
        }
    }
    return null;
}

/**
 * Fiche d'un PAV pour le rail (clic sur la carte) : nomenclature, bacs, flux, jours de collecte, journée(s) affectée(s) et
 * raison. `raisonVisite` vient du détail de journée ; une visite non affectée porte sa propre raison. Sans scénario joué,
 * seules les données de collecte sont renseignées.
 */
export function fichePav({nomenclature, collecte, calques, nonAffectees, raisonVisite = null}) {
    if (!nomenclature) return null;
    const pav = (collecte?.pavs || []).find((p) => p.nomenclature === nomenclature) ?? null;
    const lignes = pav?.collecte || [];
    const affectations = affectationsDuPav(calques, nomenclature);
    const non = (nonAffectees || []).find((n) => n.nomenclature === nomenclature) ?? null;
    return {
        nomenclature,
        bacs: pav?.quantite ?? non?.bacs ?? null,
        dechetType: pav?.dechetType ?? null,
        flux: [...new Set(lignes.map((l) => l.flux))],
        collecte: lignes.map((l) => ({flux: l.flux, texte: libelleCollecte(l), heureEstimee: l.heureEstimee ?? null})),
        affectations,
        nonAffecte: non ? {raison: non.raison ?? null} : null,
        raison: non ? (non.raison ?? null) : raisonVisite,
    };
}

/**
 * « Figer ce jour » : ajoute le verrou {nomenclature, date} au volet, sans rejouer. Si le volet porte un libellé, il est
 * suffixé « + verrou <PAV> » comme au wizard (U-7) ; sans libellé, rien n'est inventé.
 */
export function figerDansVolet(valeurs, {nomenclature, date}) {
    if ((valeurs.libelle || '').trim()) return figerJour(valeurs, {nomenclature, date});
    if (valeurs.jours.some((j) => j.nomenclature === nomenclature && j.date === date)) return valeurs;
    return {...valeurs, jours: [...valeurs.jours, {nomenclature, date}]};
}
